<script setup lang="ts">
import { formatNumber } from "chartjs-graph";
import { DataTableMetric } from "@/modules/explorer/components/Metrics/MetricResponseProcessor";
import { computed, inject } from "vue";
import { derivedDocumentKey } from "@/modules/explorer/ExplorerUtils";
import { orderComparisonData } from "chartjs-graph";
import DeltaChip from "@/modules/explorer/components/Metrics/Table/DeltaChip.vue";

const derivedDocument = inject(derivedDocumentKey);

/*
 * These values aren't available in the explorer and not needed here.
 * So I've left them out :))
 */
export type CompareTooltipMetric = Omit<
	DataTableMetric,
	"name" | "hasComparison"
>;

const {
	metric,
	showChangeAbsolute = true,
	showChangeRelativeToCurrent = true,
} = defineProps<{
	metric: CompareTooltipMetric;
	showChangeAbsolute?: boolean;
	showChangeRelativeToCurrent?: boolean;
}>();

const filteredComparisonMetrics = computed(() => {
	const filtered = metric.values.filter(
		(m) =>
			m.comparison &&
			m.comparison !== derivedDocument.value.date_comparison.comparisonLabel
	);
	return orderComparisonData(
		derivedDocument.value,
		filtered,
		(v) => v.comparison
	);
});

const baseMetric = computed(() => {
	return metric.values.find(
		(m) =>
			m.comparison === derivedDocument.value.date_comparison.comparisonLabel
	);
});
</script>

<template>
	<v-menu open-on-hover location="bottom center" offset="6">
		<template #activator="{ props }">
			<div v-bind="props" class="cursor-pointer">
				<slot>
					<v-icon class="ml-2" size="18" color="rgba(0,0,0,0.15)">
						mdi-compare
					</v-icon>
				</slot>
			</div>
		</template>

		<v-card v-if="metric" elevation="2" class="pa-3">
			<div class="tooltip-title">
				{{ derivedDocument.date_comparison.comparisonLabel }} ({{
					formatNumber(baseMetric?.totalValue, metric.format)
				}}) compared to:
			</div>
			<table class="tooltip-table">
				<tr v-for="(comparisonMetric, i) in filteredComparisonMetrics" :key="i">
					<th>
						{{ comparisonMetric?.comparison ?? comparisonMetric?.label }}
					</th>
					<td class="tooltip-table__value number-mono">
						{{ formatNumber(comparisonMetric?.totalValue, metric.format) }}
					</td>
					<td class="tooltip-table__delta">
						<delta-chip
							v-if="showChangeAbsolute || showChangeRelativeToCurrent"
							:delta-absolute="comparisonMetric.deltaAbsolute"
							:delta-relative="comparisonMetric.deltaRelative"
							:show-absolute="showChangeAbsolute"
							:show-relative="showChangeRelativeToCurrent"
							:format="metric.format"
						/>
					</td>
				</tr>
			</table>
		</v-card>
	</v-menu>
</template>

<style scoped lang="scss">
.tooltip-title {
	font-size: 0.75rem;
	font-weight: 500;
	margin-bottom: 6px;
	white-space: nowrap;
}

.tooltip-table {
	border-collapse: collapse; /* removes gaps between cells */
	width: 100%;
}

.tooltip-table th,
.tooltip-table td {
	font-weight: 400;
	font-size: 0.75rem;
	padding: 3px 4px;
	white-space: nowrap;
}

/* no dead space at the outer edges — the card's pa-3 covers that */
.tooltip-table th:first-child {
	padding-left: 0;
}

.tooltip-table td:last-child {
	padding-right: 0;
}

/* separators between rows only, so the block doesn't get boxed in */
.tooltip-table tr + tr th,
.tooltip-table tr + tr td {
	border-top: 1px solid rgba(var(--color-on-surface), 0.08);
}

/* the period label reads as a row heading, not a filled table header */
.tooltip-table th {
	text-align: left;
	color: rgba(var(--color-on-surface), 0.6);
}

/* numbers and chips right-align so each column reads as one stack */
.tooltip-table__value,
.tooltip-table__delta {
	text-align: right;
}
</style>
